/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React from 'react';
import { useLabel } from '@/hooks/useLabels';
import { formatPrice } from '@/utils/validator/formatPrice';
import { useFeatureFlag } from '@/features/globalConfig';
import { getSafeNumber } from '@/utils/common';
import cx from 'classnames';
import styles from './styles.module.scss';

type DiscountDisplayProps = {
  price: number | string;
  wasPrice: number | string;
  quantity?: number;
  isCartPage?: boolean;
  className?: string;
};

export const DiscountDisplay: React.FC<DiscountDisplayProps> = ({
  price,
  wasPrice,
  quantity = 1,
  isCartPage = false,
  className,
}) => {
  const strikethroughSave = useLabel('label_strikethrough_save');
  const isSalesPricingDisplayEnhancementsEnabled =
    useFeatureFlag('SALES_PRICING_DISPLAY_ENHANCEMENTS_ENABLED') === 'true';

  const itemPrice = getSafeNumber(wasPrice);
  const itemDiscountedPrice = getSafeNumber(price);

  if (itemDiscountedPrice === 0 || itemPrice <= itemDiscountedPrice || isCartPage) {
    return null;
  }

  const discountAmount = itemPrice - itemDiscountedPrice;
  const discountPercentage = Math.floor((discountAmount / itemPrice) * 100);

  const safeQuantity = Math.max(1, Number(quantity) || 1);
  const perUnitDiscountAmount = discountAmount / safeQuantity;
  const discountDisplay =
    isSalesPricingDisplayEnhancementsEnabled && perUnitDiscountAmount > discountPercentage
      ? `$${formatPrice(discountAmount)}`
      : `${discountPercentage}%`;

  return (
    <p
      className={cx(styles.discount, className)}
      data-m-id="strikeThroughDiscount"
    >{`${strikethroughSave} ${discountDisplay}`}</p>
  );
};

export default DiscountDisplay;
